import { ICPaasConfig } from './types';
import type { ApiError, LoginRequest, LoginResponse } from './protocol';

/**
 * Authentication for the SDK.
 *
 * The primary credential is the `Authorization: Bearer <pk_|sk_…>` header, applied to
 * every request. The cookie exchange below is kept as an optional convenience,
 * but it cannot be the mechanism: the session cookie is SameSite-scoped, so on
 * a customer's own domain the browser strips it from cross-site requests and
 * every call comes back 401. A header is not subject to that.
 */
export class AuthManager {
  private config: ICPaasConfig;
  private authenticated = false;
  private baseURL: string;

  constructor(config: ICPaasConfig) {
    this.config = config;
    // Trailing slashes would produce "//api/..." on every request.
    this.baseURL = (config.baseURL || 'http://localhost:3099').replace(/\/+$/, '');
  }

  /** Headers every authenticated request must carry. */
  headers(extra: Record<string, string> = {}): Record<string, string> {
    return { Authorization: `Bearer ${this.config.apiKey}`, ...extra };
  }

  /**
   * With an API key the Bearer header is the credential, so requests omit
   * cookies — sending them forces the CORS allow-credentials handshake, which
   * the server only grants to allowlisted origins and every other origin then
   * fails outright. Cookie mode remains for same-origin, key-less callers.
   */
  credentialsMode(): RequestCredentials {
    return this.config.apiKey ? 'omit' : 'include';
  }

  /** Verify the API key and, as a side effect, pick up a session cookie. */
  async login(): Promise<void> {
    this.log('Authenticating with API key…');

    try {
      const response = await fetch(`${this.baseURL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: this.credentialsMode(),
        body: JSON.stringify({ apiKey: this.config.apiKey } satisfies LoginRequest),
      });

      // The failure path used to assume a JSON body and threw a confusing
      // "Unexpected token" if the server answered with HTML or nothing at all.
      const data = (await response.json().catch(() => null)) as LoginResponse | ApiError | null;

      if (!response.ok) {
        throw new Error((data?.ok === false && data.error) || `Authentication failed: ${response.status}`);
      }

      const login = data?.ok ? data : null;
      this.authenticated = true;
      this.log(login?.scope === 'widget' ? 'Authenticated (publishable key: chat and voice only)' : `Authenticated as ${login?.user?.username ?? 'unknown'}`);
    } catch (error) {
      this.authenticated = false;
      if (this.config.debug) console.error('[ICPaaS SDK] Authentication failed:', error);
      throw error;
    }
  }

  async logout(): Promise<void> {
    this.log('Logging out…');
    try {
      await fetch(`${this.baseURL}/api/auth/logout`, { method: 'POST', credentials: this.credentialsMode() });
    } catch (error) {
      if (this.config.debug) console.error('[ICPaaS SDK] Logout failed:', error);
    } finally {
      this.authenticated = false;
    }
  }

  isAuthenticated(): boolean {
    return this.authenticated;
  }

  getBaseURL(): string {
    return this.baseURL;
  }

  private log(message: string): void {
    if (this.config.debug) console.log(`[ICPaaS SDK] ${message}`);
  }
}
